import {
	type Attempt,
	isCorrect,
	material,
	type ReviewQuestion,
} from "../lib/review";

type Props = {
	current: ReviewQuestion;
	currentIndex: number;
	questions: ReviewQuestion[];
	completed: number;
	correctCount: number;
	reviewCount: number;
	attempts: Record<string, Attempt>;
	chapterNumbers: number[];
	currentChapterNumber: number;
	onResetProgress: () => void;
};

export function ReviewHeader({
	current,
	currentIndex,
	questions,
	completed,
	correctCount,
	reviewCount,
	attempts,
	chapterNumbers,
	currentChapterNumber,
	onResetProgress,
}: Props) {
	return (
		<header className="topbar">
			<a
				className="brand"
				href="#review"
				aria-label="LilysAI guided review home"
			>
				<span className="brand-mark">L</span>
				<span>LilysAI</span>
			</a>
			<div className="progress-summary">
				<span>{current.chapter.chapterTitle.replace(/^\d+:\s*/, "")}</span>
				<span className="summary-divider" aria-hidden="true" />
				<span>
					Chapter {currentChapterNumber} of {chapterNumbers.length} · Question{" "}
					{currentIndex + 1} of {questions.length}
				</span>
				<span className="score correct-score">✓ {correctCount}</span>
				<span className="score review-score">↺ {reviewCount}</span>
			</div>
			<details className="progress-details">
				<summary>
					Progress details <span aria-hidden="true">⌄</span>
				</summary>
				<div className="progress-popover">
					<strong>
						{completed} of {questions.length} completed
					</strong>
					<p>
						<b>✓ {correctCount}</b> correct &nbsp; <b>↺ {reviewCount}</b> needs
						review
					</p>
					{(() => {
						const missedConcepts = [
							...new Set(
								questions
									.filter((item) => {
										const itemAttempt = attempts[item.id];
										return itemAttempt && !isCorrect(itemAttempt.status);
									})
									.flatMap((item) => [
										item.question.keyConcept,
										...(attempts[item.id]?.misconceptionTags ?? []),
									]),
							),
						];
						const reviewChapters = chapterNumbers.filter((chapterIndex) =>
							questions
								.filter((item) => item.chapterIndex === chapterIndex)
								.some((item) => {
									const itemAttempt = attempts[item.id];
									return itemAttempt && !isCorrect(itemAttempt.status);
								}),
						);
						return (
							<>
								{chapterNumbers.map((chapterIndex) => {
									const chapterQuestions = questions.filter(
										(item) => item.chapterIndex === chapterIndex,
									);
									const chapterAttempts = chapterQuestions
										.map((item) => attempts[item.id])
										.filter(Boolean);
									const hasMiss = chapterAttempts.some(
										(item) => item && !isCorrect(item.status),
									);
									const status =
										chapterAttempts.length === 0
											? "Not started"
											: hasMiss
												? "Needs review"
												: chapterAttempts.length === chapterQuestions.length
													? "Completed"
													: "In progress";
									return (
										<p key={chapterIndex} className="chapter-progress">
											<span>
												{material[chapterIndex]?.chapterTitle.replace(
													/^\d+:\s*/,
													"",
												)}
											</span>
											<b
												className={
													status === "Needs review"
														? "needs-review"
														: status === "Not started"
															? "not-started"
															: undefined
												}
											>
												{status}
											</b>
										</p>
									);
								})}
								{missedConcepts.length > 0 && (
									<p className="progress-insight">
										<b>Focus concepts:</b> {missedConcepts.join(", ")}
									</p>
								)}
								{reviewChapters.length > 0 && (
									<p className="progress-insight">
										<b>Focused review:</b>{" "}
										{reviewChapters
											.map(
												(chapterIndex) => material[chapterIndex]?.chapterTitle,
											)
											.join(", ")}
									</p>
								)}
							</>
						);
					})()}
					<button
						className="reset-progress-button"
						type="button"
						onClick={() => {
							if (
								window.confirm(
									"Reset all review progress? This cannot be undone.",
								)
							) {
								onResetProgress();
							}
						}}
					>
						Reset progress
					</button>
				</div>
			</details>
		</header>
	);
}
